import { Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartData, ChartType, Plugin } from 'chart.js';
import { jsPDF } from 'jspdf';

@Component({
  selector: 'app-chart',
  standalone: true,
  imports: [BaseChartDirective],
  templateUrl: './chart.component.html',
  styleUrl: './chart.component.scss'
})
export class ChartComponent {
  @ViewChild('chart') chartDirective?: BaseChartDirective;
  @Input() chartType: ChartType = 'bar';
  @Input() height = 320;
  @Input() chartData: {
    title?: string | null;
    xAxisTitle?: string | null;
    yAxisTitle?: string | null;
    indexAxis?: 'x' | 'y';
    categories?: string[];
    series?: Array<{ name: string; color?: string; colors?: string[]; data: number[] }>;
    data?: Array<{ name: string; y: number; color?: string }>;
    showYAxisCurrencyIcon?: boolean;
    yAxisCurrencyIconUrl?: string;
    yAxisCurrencyIconSize?: number;
    barCategoryPercentage?: number;
    barPercentage?: number;
    maxBarThickness?: number;
    minBarLength?: number;
    hideXAxisGridLines?: boolean;
    hideYAxisGridLines?: boolean;
    dashedXAxisGridLines?: boolean;
    dashedYAxisGridLines?: boolean;
    hideVerticalGridLines?: boolean;
    dashedHorizontalGridLines?: boolean;
    isRtl?: boolean;
  } | null = null;
  chartDataConfig: ChartData = { labels: [], datasets: [] };
  chartOptions: ChartConfiguration['options'] = {};
  chartPlugins: Plugin[] = [];
  private iconCache = new Map<string, HTMLImageElement>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chartType'] || changes['chartData']) {
      this.chartDataConfig = this.buildData();
      this.chartOptions = this.buildOptions();
      this.chartPlugins = this.buildPlugins();
    }
  }

  private buildData(): ChartData {
    if (this.chartType === 'pie' || this.chartType === 'doughnut') {
      const items = this.chartData?.data ?? [];
      const isDoughnut = this.chartType === 'doughnut';
      return {
        labels: items.map((item) => item.name),
        datasets: [
          {
            data: items.map((item) => item.y),
            backgroundColor: items.map((item, index) => item.color || this.getColor(index)),
            borderColor: isDoughnut ? '#ECF2FC' : '#FFFFFF',
            borderWidth: isDoughnut ? 4 : 1,
            hoverBorderColor: isDoughnut ? '#ECF2FC' : '#FFFFFF',
            hoverBorderWidth: isDoughnut ? 4 : 1,
            spacing: isDoughnut ? 2 : 0
          }
        ]
      };
    }

    const series = this.chartData?.series ?? [];
    const indexAxis = this.chartData?.indexAxis ?? 'x';
    const isHorizontalBar = this.chartType === 'bar' && indexAxis === 'y';
    const barCategoryPercentage = this.chartData?.barCategoryPercentage;
    const barPercentage = this.chartData?.barPercentage;
    const maxBarThickness = this.chartData?.maxBarThickness;
    const minBarLength = this.chartData?.minBarLength;
    return {
      labels: this.chartData?.categories ?? [],
      datasets: series.map((item, index) => ({
        label: item.name,
        data: item.data,
        backgroundColor: item.colors?.length ? item.colors : item.color || this.getColor(index),
        borderRadius: this.chartType === 'bar'
          ? (isHorizontalBar
              ? { topLeft: 0, topRight: 8, bottomLeft: 0, bottomRight: 8 }
              : { topLeft: 8, topRight: 8, bottomLeft: 0, bottomRight: 0 })
          : 0,
        borderSkipped: this.chartType === 'bar' ? (isHorizontalBar ? 'left' : 'bottom') : false,
        minBarLength: this.chartType === 'bar'
          ? (minBarLength ?? 3)
          : undefined,
        categoryPercentage: this.chartType === 'bar'
          ? (barCategoryPercentage ?? (isHorizontalBar ? 0.8 : 0.62))
          : 0.8,
        barPercentage: this.chartType === 'bar'
          ? (barPercentage ?? (isHorizontalBar ? 0.7 : 0.55))
          : 0.9,
        maxBarThickness: this.chartType === 'bar'
          ? (maxBarThickness ?? 36)
          : undefined
      }))
    };
  }

  private buildOptions(): ChartConfiguration['options'] {
    const indexAxis = this.chartData?.indexAxis ?? 'x';
    const isRtl = !!this.chartData?.isRtl;
    const iconSize = Math.max(10, Number(this.chartData?.yAxisCurrencyIconSize || 14));
    const rtlCurrencyIconRightPadding =
      this.chartType === 'bar' && this.chartData?.showYAxisCurrencyIcon && isRtl
        ? iconSize + 2
        : 0;
    return {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis,
      layout: {
        padding: {
          right: rtlCurrencyIconRightPadding
        }
      },
      ...(this.chartType === 'doughnut' ? ({ cutout: '72%' } as any) : {}),
      plugins: {
        legend: {
          display: this.chartType !== 'bar' && this.chartType !== 'doughnut',
          position: 'right',
          rtl: isRtl,
          textDirection: isRtl ? 'rtl' : 'ltr'
        },
        title: {
          display: !!this.chartData?.title,
          text: this.chartData?.title ?? ''
        },
        tooltip: {
          rtl: isRtl
        }
      },
      scales: this.chartType === 'bar'
        ? {
            x: indexAxis === 'y'
              ? {
                  beginAtZero: true,
                  reverse: isRtl,
                  grid: {
                    display: !this.chartData?.hideXAxisGridLines && !this.chartData?.hideVerticalGridLines,
                    borderDash: this.chartData?.dashedXAxisGridLines || this.chartData?.dashedHorizontalGridLines
                      ? [5, 6]
                      : undefined
                  },
                  title: {
                    display: !!this.chartData?.xAxisTitle,
                    text: this.chartData?.xAxisTitle ?? ''
                  }
                }
              : {
                  reverse: isRtl,
                  grid: {
                    display: !this.chartData?.hideXAxisGridLines && !this.chartData?.hideVerticalGridLines,
                    borderDash: this.chartData?.dashedXAxisGridLines
                      ? [5, 6]
                      : undefined
                  },
                  title: {
                    display: !!this.chartData?.xAxisTitle,
                    text: this.chartData?.xAxisTitle ?? ''
                  }
                },
            y: indexAxis === 'y'
              ? {
                  position: isRtl ? 'right' : 'left',
                  grid: {
                    display: !this.chartData?.hideYAxisGridLines,
                    borderDash: this.chartData?.dashedYAxisGridLines
                      ? [5, 6]
                      : undefined
                  },
                  title: {
                    display: !!this.chartData?.yAxisTitle,
                    text: this.chartData?.yAxisTitle ?? ''
                  }
                }
              : {
                  beginAtZero: true,
                  position: isRtl ? 'right' : 'left',
                  grid: {
                    display: !this.chartData?.hideYAxisGridLines,
                    borderDash: this.chartData?.dashedHorizontalGridLines ? [5, 6] : undefined
                  },
                  ticks: {
                    padding: this.getYAxisTickPadding(),
                    callback: (value: any) => this.formatCompactTick(value)
                  },
                  title: {
                    display: !!this.chartData?.yAxisTitle,
                    text: this.chartData?.yAxisTitle ?? ''
                  }
                }
          }
        : undefined
    };
  }

  private buildPlugins(): Plugin[] {
    if (this.chartType !== 'bar' || !this.chartData?.showYAxisCurrencyIcon || !this.chartData?.yAxisCurrencyIconUrl) {
      return [];
    }

    const iconUrl = this.chartData.yAxisCurrencyIconUrl;
    const icon = this.getOrCreateIcon(iconUrl);

    const plugin: Plugin = {
      id: 'yAxisCurrencyIcon',
      afterDraw: (chart) => {
        const yAxis: any = (chart as any).scales?.['y'];
        if (!yAxis || !icon.complete || !icon.naturalWidth) {
          return;
        }

        const ctx = chart.ctx;
        const size = Math.max(10, Number(this.chartData?.yAxisCurrencyIconSize || 14));
        const isRightAxis = (yAxis.options?.position ?? 'left') === 'right';
        // Keep the icon near tick labels: before labels in LTR, after labels in RTL/right axis.
        const x = isRightAxis
          ? Math.min(chart.width - size - 2, Math.max(2, yAxis.right + 10))
          : Math.max(2, yAxis.left + 4);

        ctx.save();
        for (let i = 0; i < yAxis.ticks.length; i++) {
          // Nudge up slightly for better visual centering with tick text.
          const y = yAxis.getPixelForTick(i) - (size / 2) - 1;
          ctx.drawImage(icon, x, y, size, size);
        }
        ctx.restore();
      }
    };

    return [plugin];
  }

  private getOrCreateIcon(url: string): HTMLImageElement {
    const existing = this.iconCache.get(url);
    if (existing) {
      return existing;
    }

    const image = new Image();
    image.src = url;
    this.iconCache.set(url, image);
    return image;
  }

  private formatCompactTick(value: any): string {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return '';
    }
    if (numeric >= 1000) {
      const compact = numeric / 1000;
      const decimals = compact % 1 === 0 ? 0 : 1;
      return `${compact.toFixed(decimals)}K`;
    }
    return `${Math.round(numeric)}`;
  }

  private getYAxisTickPadding(): number {
    if (this.chartType === 'bar' && this.chartData?.showYAxisCurrencyIcon) {
      if (this.chartData?.isRtl) {
        return 6;
      }
      const size = Math.max(10, Number(this.chartData?.yAxisCurrencyIconSize || 14));
      return size + 3;
    }
    return 6;
  }

  private getColor(index: number): string {
    const palette = [
      '#3A8DDE',
      '#003764',
      '#E04403',
      '#009933',
      '#76BC21',
      '#006699',
      '#D7282F',
      '#DDE7EB',
      '#666666'
    ];
    return palette[index % palette.length];
  }

  downloadPdf(fileName: string) {
    const image = this.chartDirective?.toBase64Image();
    if (!image) {
      return;
    }

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    const maxWidth = pageWidth - margin * 2;
    const maxHeight = pageHeight - margin * 2;
    const imgProps = doc.getImageProperties(image);
    const imgRatio = imgProps.width / imgProps.height;
    let imgWidth = maxWidth;
    let imgHeight = imgWidth / imgRatio;

    if (imgHeight > maxHeight) {
      imgHeight = maxHeight;
      imgWidth = imgHeight * imgRatio;
    }

    const x = (pageWidth - imgWidth) / 2;
    const y = (pageHeight - imgHeight) / 2;
    doc.addImage(image, 'PNG', x, y, imgWidth, imgHeight);
    doc.save(`${fileName}.pdf`);
  }
}
